import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useLocations, type Location } from '@/features/inventory/hooks';
import {
  DISPOSITIONS,
  DISPOSITION_LABELS,
  ITEM_CONDITIONS,
  ITEM_CONDITION_LABELS,
  returnLineLabel,
  useReceiveReturn,
  type Disposition,
  type ItemCondition,
  type ReturnReason,
  type ReturnRow,
} from '@/features/returns/hooks';
import { todayIsoDate } from '@/lib/formatters';

const NONE = '__none';

/** Reasons where the unit shouldn't go straight back on the shelf. */
const SUSPECT_REASONS: ReturnReason[] = ['damaged_in_transit', 'defective', 'missing_parts', 'warranty'];

interface ReceiveLineDraft {
  returnLineId: string;
  label: string;
  sku: string | null;
  mapped: boolean;
  isLotTracked: boolean;
  authorized: number;
  alreadyReceived: number;
  quantity: string;
  disposition: Disposition;
  condition: ItemCondition | '';
  locationId: string;
  lotNumber: string;
}

function defaultLocation(locations: Location[], disposition: Disposition): string {
  if (disposition === 'scrap') return '';
  const wanted = disposition === 'restock' ? 'warehouse' : 'quarantine';
  return (
    locations.find((l) => l.kind === wanted)?.id ??
    locations.find((l) => l.kind === 'warehouse')?.id ??
    locations[0]?.id ??
    ''
  );
}

interface ReceiveReturnDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rma: ReturnRow | null;
}

/**
 * Check in a returned shipment. Each line gets the condition it arrived in
 * and a disposition: restock adds sellable on-hand, inspect / return-to-
 * vendor add on-hand at a quarantine location, scrap records the receipt
 * without adding stock.
 */
export function ReceiveReturnDialog({ open, onOpenChange, rma }: ReceiveReturnDialogProps) {
  const receive = useReceiveReturn();
  const { data: locationData } = useLocations();
  const locations = useMemo(() => locationData ?? [], [locationData]);

  const [receivedAt, setReceivedAt] = useState(todayIsoDate());
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<ReceiveLineDraft[]>([]);

  // Seed once per RMA per open (and again once locations arrive), not on
  // every refetch, which would wipe edits.
  const builtFor = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !rma) {
      builtFor.current = null;
      return;
    }
    const buildKey = `${rma.id}:${locations.length > 0}`;
    if (builtFor.current === buildKey) return;
    builtFor.current = buildKey;
    setReceivedAt(todayIsoDate());
    setNotes('');
    const suspect = SUSPECT_REASONS.includes(rma.reason_code);
    setLines(
      rma.return_lines.map((l) => {
        const outstanding = Math.max(0, l.quantity - l.received_quantity);
        const disposition: Disposition = !l.item_id
          ? 'scrap'
          : l.disposition ?? (suspect ? 'inspect' : 'restock');
        return {
          returnLineId: l.id,
          label: returnLineLabel(l),
          sku: l.items?.sku ?? l.sku,
          mapped: !!l.item_id,
          isLotTracked: l.items?.is_lot_tracked ?? false,
          authorized: l.quantity,
          alreadyReceived: l.received_quantity,
          quantity: outstanding > 0 ? String(outstanding) : '',
          disposition,
          condition: '',
          locationId: defaultLocation(locations, disposition),
          lotNumber: '',
        };
      }),
    );
  }, [open, rma, locations]);

  const updateLine = (id: string, patch: Partial<ReceiveLineDraft>) =>
    setLines((prev) => prev.map((l) => (l.returnLineId === id ? { ...l, ...patch } : l)));

  const receivingCount = lines.filter((l) => Number(l.quantity) > 0).length;
  const completesRma =
    lines.length > 0 && lines.every((l) => l.alreadyReceived + (Number(l.quantity) || 0) >= l.authorized);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rma) return;
    const payload = [];
    try {
      for (const l of lines) {
        if (l.quantity.trim() === '') continue;
        const qty = Number(l.quantity);
        if (!Number.isInteger(qty) || qty < 0) throw new Error(`${l.label}: quantity must be a whole number.`);
        if (qty === 0) continue;
        if (l.alreadyReceived + qty > l.authorized) {
          throw new Error(`${l.label}: only ${l.authorized - l.alreadyReceived} still expected on this RMA.`);
        }
        if (l.disposition !== 'scrap' && !l.locationId) {
          throw new Error(`${l.label}: pick a location to receive into.`);
        }
        payload.push({
          return_line_id: l.returnLineId,
          quantity: qty,
          disposition: l.disposition,
          condition: l.condition || null,
          to_location_id: l.disposition === 'scrap' ? null : l.locationId,
          lot_number: l.isLotTracked && l.disposition !== 'scrap' ? l.lotNumber || null : null,
        });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Check the receiving lines.');
      return;
    }
    if (payload.length === 0) {
      toast.error('Enter a received quantity on at least one line.');
      return;
    }

    try {
      await receive.mutateAsync({
        returnId: rma.id,
        // Noon local keeps the calendar date stable across time zones.
        receivedAt: new Date(`${receivedAt}T12:00:00`).toISOString(),
        notes: notes || null,
        lines: payload,
      });
      toast.success(
        completesRma ? `${rma.rma_number} fully received` : `Received ${payload.length} line${payload.length === 1 ? '' : 's'} on ${rma.rma_number}`,
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not post the return receipt.');
    }
  };

  const submitting = receive.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Receive return {rma?.rma_number}</DialogTitle>
          </DialogHeader>

          <div className="mt-5 space-y-6">
            <div className="max-w-xs space-y-1.5">
              <Label htmlFor="rr-date">Received on</Label>
              <Input
                id="rr-date"
                type="date"
                value={receivedAt}
                max={todayIsoDate()}
                onChange={(e) => setReceivedAt(e.target.value || todayIsoDate())}
              />
            </div>

            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Item</th>
                    <th className="w-24 px-2 py-2 text-right font-medium">RMA / recv'd</th>
                    <th className="w-20 px-2 py-2 text-right font-medium">Receiving</th>
                    <th className="w-36 px-2 py-2 text-left font-medium">Condition</th>
                    <th className="w-44 px-2 py-2 text-left font-medium">Disposition</th>
                    <th className="w-44 px-2 py-2 text-left font-medium">Into location</th>
                    <th className="w-28 px-2 py-2 text-left font-medium">Lot</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={l.returnLineId} className="border-t border-border align-top">
                      <td className="px-3 py-2">
                        {l.sku && <div className="font-mono text-xs text-muted-foreground">{l.sku}</div>}
                        <div className="truncate">{l.label}</div>
                        {!l.mapped && (
                          <div className="text-[11px] text-fresh-salmon-dark">
                            Not mapped to a catalog item — scrap only. Edit the RMA to map it.
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                        {l.authorized} / {l.alreadyReceived}
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          value={l.quantity}
                          onChange={(e) => updateLine(l.returnLineId, { quantity: e.target.value.replace(/[^\d]/g, '') })}
                          inputMode="numeric"
                          placeholder="0"
                          aria-label={`Quantity received for ${l.label}`}
                          className="h-8 text-right tabular-nums"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <Select
                          value={l.condition || NONE}
                          onValueChange={(v) =>
                            updateLine(l.returnLineId, { condition: v === NONE ? '' : (v as ItemCondition) })
                          }
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>Not noted</SelectItem>
                            {ITEM_CONDITIONS.map((c) => (
                              <SelectItem key={c} value={c}>
                                {ITEM_CONDITION_LABELS[c]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-2">
                        <Select
                          value={l.disposition}
                          disabled={!l.mapped}
                          onValueChange={(v) => {
                            const disposition = v as Disposition;
                            updateLine(l.returnLineId, {
                              disposition,
                              locationId: defaultLocation(locations, disposition),
                            });
                          }}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DISPOSITIONS.map((d) => (
                              <SelectItem key={d} value={d}>
                                {DISPOSITION_LABELS[d]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-2">
                        {l.disposition === 'scrap' ? (
                          <span className="text-xs text-muted-foreground">No stock added</span>
                        ) : (
                          <Select
                            value={l.locationId}
                            onValueChange={(v) => updateLine(l.returnLineId, { locationId: v })}
                          >
                            <SelectTrigger className="h-8" aria-label={`Location for ${l.label}`}>
                              <SelectValue placeholder="Pick a location" />
                            </SelectTrigger>
                            <SelectContent>
                              {locations.map((loc) => (
                                <SelectItem key={loc.id} value={loc.id}>
                                  {loc.name} ({loc.code})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        {l.isLotTracked && l.disposition !== 'scrap' ? (
                          <Input
                            value={l.lotNumber}
                            onChange={(e) => updateLine(l.returnLineId, { lotNumber: e.target.value })}
                            placeholder="Lot #"
                            maxLength={120}
                            className="h-8 font-mono text-xs"
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!locations.some((l) => l.kind === 'quarantine') && (
              <p className="rounded-md border border-dashed border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
                No quarantine location yet.
              </p>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="rr-notes">Notes</Label>
              <Textarea
                id="rr-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Packaging condition, missing accessories, photos taken…"
                rows={2}
                maxLength={2000}
              />
            </div>
          </div>

          <DialogFooter className="mt-6 sm:items-center">
            {receivingCount > 0 && (
              <span className="mr-auto text-xs text-muted-foreground">
                {completesRma ? 'This completes the RMA — it will be marked Received.' : 'The RMA will be marked Partially received.'}
              </span>
            )}
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !rma || receivingCount === 0}>
              {submitting ? 'Posting…' : 'Post return receipt'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
